import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PaymentOutcomeActions,
  PaymentOutcomeLayout,
} from "investment/PaymentOutcomeLayout";
import {
  clearPendingCheckout,
  extractCheckoutPayload,
  isFailedInvestmentStatus,
  isPaidInvestmentStatus,
  readPendingCheckout,
  syncInvestmentSession,
  getStripeSessionId,
} from "lib/investmentHelper";

export default function InvestmentPaymentFailedPage() {
  const [searchParams] = useSearchParams();
  const [description, setDescription] = useState(
    "Your payment could not be completed. Please try again or use a different payment method."
  );
  const [projectId, setProjectId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    const pending = readPendingCheckout();
    const campaignFromQuery =
      searchParams.get("project_id") || searchParams.get("campaignId");
    setProjectId(campaignFromQuery || pending?.projectId || null);
    setAmount(pending?.amount ?? null);

    const sessionId = getStripeSessionId(searchParams);
    if (!sessionId || ranRef.current) {
      if (!sessionId) clearPendingCheckout();
      return;
    }

    ranRef.current = true;
    setLoading(true);

    (async () => {
      try {
        const resp = await syncInvestmentSession(sessionId);
        const payload = extractCheckoutPayload(resp);

        if (isPaidInvestmentStatus(payload.status)) {
          setDescription(
            payload.message ||
              "Your payment appears to have gone through. Check your dashboard for details."
          );
        } else if (isFailedInvestmentStatus(payload.status)) {
          setDescription(
            payload.message ||
              "Your payment was not completed. You can try investing again when ready."
          );
        } else {
          setDescription(
            payload.message ||
              "Your payment could not be completed. Please try again or contact support."
          );
        }
      } catch (err) {
        setDescription(
          err?.message ||
            "Your payment could not be completed. Please try again or contact support."
        );
      } finally {
        clearPendingCheckout();
        setLoading(false);
      }
    })();
  }, [searchParams]);

  if (loading) {
    return (
      <PaymentOutcomeLayout
        variant="loading"
        pageTitle="Checking payment"
        title="Checking payment status"
        description="Please wait…"
      />
    );
  }

  return (
    <PaymentOutcomeLayout
      variant="failed"
      pageTitle="Payment failed"
      title="Payment failed"
      description={description}
      primaryAction={
        <PaymentOutcomeActions
          campaignId={projectId}
          amount={amount}
          showRetry={Boolean(projectId)}
        />
      }
    />
  );
}
